import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Button,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Fab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import iconShadow from "leaflet/dist/images/marker-shadow.png";
import seenIconUrl from "../images/ic_deploy_blue.png";
import bronzeIconUrl from "../images/ic_deploy_bronze.png";
import silverIconUrl from "../images/ic_deploy_silver.png";
import goldIconUrl from "../images/ic_deploy_gold.png";
import clickMarkerUrl from "../images/ic_deploy_marker.png";

// Icons auf Modulebene erstellen – nicht bei jedem Render neu erzeugen
const ICON_SIZE = [60, 52];
const ICON_ANCHOR = [30, 52];
const POPUP_ANCHOR = [0, -52];

const ICONS = {
  seen: L.icon({ iconUrl: seenIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  bronze: L.icon({ iconUrl: bronzeIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  silber: L.icon({ iconUrl: silverIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  gold: L.icon({ iconUrl: goldIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  click: L.icon({ iconUrl: clickMarkerUrl, shadowUrl: iconShadow, iconSize: [70, 60], iconAnchor: [35, 60], popupAnchor: [0, -60] }),
};

const isInputSafe = (text) => {
  const forbiddenPattern = /[<>/\\'"`;{}]/;
  return typeof text === "string" && !forbiddenPattern.test(text.trim());
};

// Automatisch zur Nutzerposition fliegen wenn Karte geladen ist
const MapAutoLocate = () => {
  const map = useMap();
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => map.flyTo([pos.coords.latitude, pos.coords.longitude], 15),
      () => {} // Kein GPS → kein Problem, startet in Deutschland-Mitte
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const MapClickHandler = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      const target = e.originalEvent.target;
      if (
        !target.closest(".leaflet-control") &&
        !target.closest(".leaflet-marker-icon") &&
        !target.closest(".locate-fab") &&
        !target.closest(".close-map-btn")
      ) {
        onClick(e.latlng);
      }
    },
  });
  return null;
};

const LocateButton = () => {
  const map = useMap();
  const handleLocate = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => map.flyTo([pos.coords.latitude, pos.coords.longitude], 18),
      () => {} // Fehler lautlos ignorieren
    );
  };
  return (
    <Fab
      size="medium"
      onClick={handleLocate}
      className="locate-fab"
      sx={{
        position: "absolute",
        bottom: 90,
        right: 16,
        zIndex: 1000,
        backgroundColor: "white",
        color: "black",
        boxShadow: 3,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      <GpsFixedIcon />
    </Fab>
  );
};

const Arenamap = () => {
  const [gyms, setGyms] = useState([]);
  const [formState, setFormState] = useState({ name: "", arenatype: "bronze", note: "" });
  const [clickedPosition, setClickedPosition] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [currentGymId, setCurrentGymId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);

  // Löschen-Bestätigung per Dialog (kein window.confirm)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Snackbar statt alert()
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const navigate = useNavigate();
  const mapRef = useRef(null);
  const initialMapPosition = [51.1657, 10.4515];

  useEffect(() => {
    fetchGyms();
  }, []);

  const fetchGyms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/users/get-gyms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setGyms(await response.json());
      } else if (response.status === 401) {
        navigate("/login");
      }
    } catch (err) {
      console.error("Fehler beim Laden der Arenen:", err);
    }
  };

  const panToPosition = useCallback((latlng) => {
    if (!mapRef.current) return;
    mapRef.current.panTo(latlng);
  }, []);

  const handleMapClick = (latlng) => {
    setModalMode("create");
    setFormState({ name: "", arenatype: "bronze", note: "" });
    setClickedPosition(latlng);
    setShowModal(true);
    setIsEditing(true);
    setViewOnly(false);
    panToPosition(latlng);
  };

  const handleMarkerClick = (gym) => {
    setModalMode("edit");
    setFormState({ name: gym.gymname, arenatype: gym.type, note: gym.note || "" });
    setClickedPosition(null);
    setCurrentGymId(gym.id);
    setShowModal(true);
    setIsEditing(false);
    setViewOnly(true);
    panToPosition({ lat: gym.latitude, lng: gym.longitude });
  };

  const handleDeleteGym = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/users/delete-gym?gymId=${currentGymId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        closeModal();
        fetchGyms();
        showSnack("Arena gelöscht.");
      } else {
        showSnack("Fehler beim Löschen der Arena.", "error");
      }
    } catch {
      showSnack("Fehler beim Löschen der Arena.", "error");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    const isCreate = modalMode === "create";
    const url = isCreate ? "/api/users/create-gym" : "/api/users/edit-gym";
    const method = isCreate ? "POST" : "PUT";

    const body = isCreate
      ? {
          gymname: formState.name,
          type: formState.arenatype,
          note: formState.note,
          latitude: clickedPosition.lat,
          longitude: clickedPosition.lng,
        }
      : {
          gymId: currentGymId,
          gymname: formState.name,
          type: formState.arenatype,
          note: formState.note,
        };

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        closeModal();
        fetchGyms();
        showSnack(isCreate ? "Arena hinzugefügt!" : "Arena gespeichert!");
      } else {
        const msg = await response.text();
        showSnack("Fehler: " + msg, "error");
      }
    } catch {
      showSnack("Fehler beim Speichern.", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setClickedPosition(null);
    setIsEditing(false);
    setViewOnly(false);
  };

  const handleModalBack = () => {
    // Im Edit-Modus: zurück zur Ansicht statt schließen
    if (modalMode === "edit" && isEditing) {
      setIsEditing(false);
      setViewOnly(true);
    } else {
      closeModal();
    }
  };

  const nameError = formState.name.length > 0 && !isInputSafe(formState.name);
  const noteError = formState.note.length > 0 && !isInputSafe(formState.note);
  const canSave =
    formState.name.trim().length > 0 && !nameError && !noteError;

  return (
    <div style={{ position: "relative", height: "100dvh", width: "100%", overflow: "hidden" }}>
      {/* Karte */}
      <MapContainer
        ref={mapRef}
        center={initialMapPosition}
        zoom={6}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <MapAutoLocate />
        <MapClickHandler onClick={handleMapClick} />
        <LocateButton />

        {gyms.map((gym) => (
          <Marker
            key={gym.id}
            position={[gym.latitude, gym.longitude]}
            icon={ICONS[gym.type] || ICONS.seen}
            eventHandlers={{ click: () => handleMarkerClick(gym) }}
          />
        ))}

        {modalMode === "create" && clickedPosition && (
          <Marker position={clickedPosition} icon={ICONS.click} />
        )}
      </MapContainer>

      {/* Zurück-Button */}
      <IconButton
        onClick={() => navigate("/profile")}
        className="close-map-btn"
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 1000,
          backgroundColor: "white",
          color: "error.main",
          boxShadow: 3,
          "&:hover": { backgroundColor: "#ffeaea" },
        }}
      >
        <HighlightOffIcon sx={{ fontSize: "2rem" }} />
      </IconButton>

      {/* Arena-Modal (Bottom Sheet Stil auf Mobile) */}
      {showModal && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: "background.paper",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.18)",
            p: 3,
            pt: 2,
            maxHeight: "80dvh",
            overflowY: "auto",
          }}
        >
          {/* Drag-Handle */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: "divider",
              }}
            />
          </Box>

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {modalMode === "edit" && isEditing && (
                <IconButton size="small" onClick={handleModalBack}>
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h6">
                {modalMode === "edit"
                  ? viewOnly
                    ? "Arena"
                    : "Arena bearbeiten"
                  : "Neue Arena"}
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Formular */}
          <TextField
            label="Name"
            name="name"
            value={formState.name}
            onChange={handleInputChange}
            disabled={viewOnly}
            error={nameError}
            helperText={nameError ? "Ungültige Zeichen enthalten" : ""}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Select
            name="arenatype"
            value={formState.arenatype}
            onChange={handleInputChange}
            disabled={viewOnly}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="seen">Gesehen</MenuItem>
            <MenuItem value="bronze">Bronze</MenuItem>
            <MenuItem value="silber">Silber</MenuItem>
            <MenuItem value="gold">Gold</MenuItem>
          </Select>

          <TextField
            label="Notiz (optional)"
            name="note"
            value={formState.note}
            onChange={handleInputChange}
            disabled={viewOnly}
            error={noteError}
            helperText={noteError ? "Ungültige Zeichen enthalten" : ""}
            multiline
            rows={2}
            fullWidth
            sx={{ mb: 2.5 }}
          />

          {/* Aktionen */}
          {viewOnly ? (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setIsEditing(true);
                  setViewOnly(false);
                }}
                sx={{ flex: 1 }}
              >
                Bearbeiten
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteGym}
              >
                Löschen
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!canSave}
                onClick={handleSave}
                sx={{ flex: 1 }}
              >
                Speichern
              </Button>
              <Button variant="outlined" onClick={handleModalBack}>
                Abbrechen
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Löschen-Bestätigung */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Arena löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du <strong>{formState.name}</strong> wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Arenamap;
